import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Plus, Loader2, AlertCircle, AlertTriangle, Trash2,
    RefreshCw, Check, Pencil, DoorOpen, Square,
} from 'lucide-react';
import HouseFloorPlanService from '../../services/HouseFloorPlanService';
import UserFloorPlanService from '../../services/UserFloorPlanService';
import HouseService from '../../services/HouseService';
import FloorPlanViewer from '../../components/v2/FloorPlanViewer';
import FloorPlanErrorBoundary from '../../components/v2/FloorPlanErrorBoundary';
import SceneObjectList from '../../components/v2/SceneObjectList';
import { Card, Button, Input, Toggle, ConfirmationModal, toast } from '../../components/v2/V2Components';
import {
    DEFAULT_WALL_THICKNESS_M,
    IDENTITY_TRANSFORM,
    buildPlacementPayload,
    isOverlapSignificant,
    multiplyMatrices,
    placedFloorPolygon,
    polygonOverlapArea,
    solvePairingTransform,
} from '../../utils/pairingSolver';
import { polygonArea } from '../../utils/floorPlanGeometry';
import {
    UNIT_SYSTEMS, formatArea, formatLinearMeasurement,
} from '../../utils/measurementUtils';

const EMPTY_HIDDEN = new Set();

// Surface types a Pairing may reference (mirrors the backend validator).
const PAIRABLE_KEYS = ['walls', 'doors', 'openings'];

/** Flatten a viewer `data` blob into selectable pairing candidates. */
const pairableSurfaces = (data) => {
    if (!data) return [];
    return PAIRABLE_KEYS.flatMap((key) =>
        (data[key] || []).map((surface) => ({
            surface,
            identifier: surface.identifier,
            placementId: surface.placementId,
            type: key === 'walls' ? 'wall' : key === 'doors' ? 'door' : 'opening',
        })),
    );
};

/** Centroid of a placed room's floor footprint (floor frame, XZ). */
const placementInteriorPoint = (data, placementId) => {
    const floor = (data?.floors || []).find((f) => f.placementId === placementId);
    if (!floor) return null;
    const poly = placedFloorPolygon(floor);
    if (poly.length === 0) return null;
    const cx = poly.reduce((acc, [x]) => acc + x, 0) / poly.length;
    const cz = poly.reduce((acc, [, z]) => acc + z, 0) / poly.length;
    return [cx, 0, cz];
};

/** Reflect a point across a wall's plane (XZ only) — the "other side" toggle. */
const reflectAcrossSurface = (point, surface) => {
    const t = surface?.transform;
    if (!Array.isArray(t) || t.length < 15) return point;
    const n = [t[8], t[10]]; // wall normal, XZ components
    const c = [t[12], t[14]];
    const d = (point[0] - c[0]) * n[0] + (point[2] - c[1]) * n[1];
    return [point[0] - 2 * d * n[0], point[1], point[2] - 2 * d * n[1]];
};

/** Merge an incoming plan's surfaces (transformed by `placement`) into a
 *  copy of the floor's data blob, for live preview. */
const mergePreviewData = (floorData, incomingData, placement) => {
    const merged = { ...floorData };
    ['walls', 'doors', 'windows', 'openings', 'floors', 'objects'].forEach((key) => {
        const incoming = (incomingData?.[key] || [])
            .filter((s) => Array.isArray(s.transform) && s.transform.length === 16)
            .map((s) => ({ ...s, transform: multiplyMatrices(placement, s.transform), isGhost: true }));
        merged[key] = [...(floorData?.[key] || []), ...incoming];
    });
    return merged;
};

const SurfacePickList = ({ candidates, selectedId, onPick, unitSystem }) => (
    <div className="space-y-1 max-h-64 overflow-y-auto">
        {candidates.map(({ identifier, type, surface }) => (
            <button
                key={identifier}
                onClick={() => onPick(identifier)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    selectedId === identifier
                        ? 'bg-[var(--primary)] text-white'
                        : 'bg-[var(--bg-hover)] text-[var(--text-main)] hover:border-[var(--primary)]'
                }`}
            >
                {type === 'door' ? <DoorOpen size={14} /> : <Square size={14} />}
                <span className="capitalize">{type}</span>
                <span className="ml-auto opacity-75">
                    {formatLinearMeasurement(surface.dimensions?.[0], unitSystem)} wide
                </span>
            </button>
        ))}
        {candidates.length === 0 && (
            <p className="text-sm text-[var(--text-muted)] px-1 py-2">No pairable walls or doors found.</p>
        )}
    </div>
);

const StepHeading = ({ children }) => (
    <p className="text-xs font-semibold text-[var(--text-secondary)] uppercase mb-2">{children}</p>
);

const HouseFloorPlanEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [floorPlan, setFloorPlan] = useState(null);
    const [house, setHouse] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // Viewer plumbing (mirrors RoomDetails).
    const [sceneData, setSceneData] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [hiddenIds, setHiddenIds] = useState(EMPTY_HIDDEN);
    const [unitSystem, setUnitSystem] = useState(UNIT_SYSTEMS.INCHES);

    // Add-room flow.
    const [adding, setAdding] = useState(false);
    const [incomingPlan, setIncomingPlan] = useState(null); // full plan with data blob
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [anchorId, setAnchorId] = useState(null); // placed-side surface identifier
    const [incomingId, setIncomingId] = useState(null); // incoming-side identifier
    const [thicknessIn, setThicknessIn] = useState(String(DEFAULT_WALL_THICKNESS_M));
    const [slideIn, setSlideIn] = useState('0');
    const [flipped, setFlipped] = useState(false);
    const [otherSide, setOtherSide] = useState(false);
    const [saving, setSaving] = useState(false);

    // Overview actions.
    const [realigningId, setRealigningId] = useState(null);
    const [placementToRemove, setPlacementToRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [showDeletePlan, setShowDeletePlan] = useState(false);
    const [deletingPlan, setDeletingPlan] = useState(false);
    // ConfirmationModal calls onConfirm() then onClose() in the SAME click
    // tick. State setters (setRemoving/setDeletingPlan) don't update until
    // the next render, so an onClose guard reading those booleans still sees
    // false and the modal closes before the loading state can show. A ref
    // mutates synchronously, so the guard sees the in-flight state this tick.
    const removingRef = useRef(false);
    const deletingPlanRef = useRef(false);
    const [editingLabel, setEditingLabel] = useState(false);
    const [labelDraft, setLabelDraft] = useState('');

    const fetchFloorPlan = useCallback(async () => {
        try {
            const data = await HouseFloorPlanService.getFloorPlan(id);
            setFloorPlan(data);
            setError(null);
            return data;
        } catch (err) {
            setError(err.message || 'Failed to load floor plan');
            return null;
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchFloorPlan();
    }, [fetchFloorPlan]);

    useEffect(() => {
        if (!floorPlan?.house_id) return;
        HouseService.getHouse(floorPlan.house_id)
            .then(setHouse)
            .catch(() => setHouse(null)); // header degrades to label-only
    }, [floorPlan?.house_id]);

    const placements = floorPlan?.placements || [];
    const isBasePlacement = placements.length === 0;
    const placedRoomIds = useMemo(() => new Set(placements.map((p) => p.room_id)), [placements]);

    // Rooms available to add: house rooms not yet placed; matching floor first.
    const candidateRooms = useMemo(() => {
        const rooms = (house?.rooms || []).filter((r) => !placedRoomIds.has(r.id));
        const label = floorPlan?.floor_label;
        return [...rooms].sort((a, b) => (b.floor === label) - (a.floor === label));
    }, [house, placedRoomIds, floorPlan?.floor_label]);

    const anchorCandidates = useMemo(() => pairableSurfaces(floorPlan?.data), [floorPlan]);
    const incomingCandidates = useMemo(() => pairableSurfaces(incomingPlan?.data), [incomingPlan]);
    const anchor = anchorCandidates.find((c) => c.identifier === anchorId) || null;
    const incoming = incomingCandidates.find((c) => c.identifier === incomingId) || null;

    const thicknessM = Number.parseFloat(thicknessIn);
    const slideM = Number.parseFloat(slideIn);

    // The live preview: solve the pairing and merge the incoming room in.
    const preview = useMemo(() => {
        if (!adding || !incomingPlan) return null;
        if (isBasePlacement) {
            return {
                transform: [...IDENTITY_TRANSFORM],
                data: mergePreviewData(floorPlan?.data, incomingPlan.data, [...IDENTITY_TRANSFORM]),
                overlaps: false,
            };
        }
        if (!anchor || !incoming) return null;
        let interior = placementInteriorPoint(floorPlan?.data, anchor.placementId);
        if (interior && otherSide) interior = reflectAcrossSurface(interior, anchor.surface);
        const transform = solvePairingTransform({
            placedSurface: anchor.surface,
            incomingSurface: incoming.surface,
            thicknessM: Number.isFinite(thicknessM) ? thicknessM : DEFAULT_WALL_THICKNESS_M,
            slideM: Number.isFinite(slideM) ? slideM : 0,
            flipped,
            placedInteriorPoint: interior,
        });

        // Overlap warning: incoming floors vs every placed floor.
        let overlaps = false;
        const placedFloors = (floorPlan?.data?.floors || []).map((f) => placedFloorPolygon(f));
        (incomingPlan.data?.floors || []).forEach((f) => {
            const incomingPoly = placedFloorPolygon(f, transform);
            placedFloors.forEach((placedPoly) => {
                const overlap = polygonOverlapArea(incomingPoly, placedPoly);
                if (isOverlapSignificant(overlap, polygonArea(incomingPoly), polygonArea(placedPoly))) {
                    overlaps = true;
                }
            });
        });

        return { transform, data: mergePreviewData(floorPlan?.data, incomingPlan.data, transform), overlaps };
    }, [adding, incomingPlan, isBasePlacement, anchor, incoming, thicknessM, slideM, flipped, otherSide, floorPlan]);

    // Memoized: FloorPlanViewer keys its load effect on previewData's
    // IDENTITY — a fresh object every render would loop setScanData.
    const viewerData = useMemo(
        () => (preview ? { ...floorPlan, data: preview.data } : floorPlan),
        [preview, floorPlan],
    );

    const resetAddFlow = () => {
        setAdding(false);
        setIncomingPlan(null);
        setAnchorId(null);
        setIncomingId(null);
        setThicknessIn(String(DEFAULT_WALL_THICKNESS_M));
        setSlideIn('0');
        setFlipped(false);
        setOtherSide(false);
    };

    // Pick a room: resolve its Room Plan, auto-converting the latest scan
    // when the room has no plan yet (the merge consumes plans only).
    const handlePickRoom = async (room) => {
        setLoadingPlan(true);
        try {
            const { plans } = (await UserFloorPlanService.getPlansForRoom(room.id)) || {};
            let planId = plans?.[0]?.id;
            if (!planId) {
                const created = await UserFloorPlanService.createFromScan({ room_id: room.id });
                planId = created.id;
                toast.success(`Converted ${room.name}'s scan into a plan`);
            }
            const fullPlan = await UserFloorPlanService.getPlan(planId);
            setIncomingPlan(fullPlan);
        } catch (err) {
            toast.error(err.message || `${room.name} has no floor plan or scan to merge`);
        } finally {
            setLoadingPlan(false);
        }
    };

    const handleConfirmPlacement = async () => {
        if (!preview || !incomingPlan) return;
        setSaving(true);
        try {
            const payload = buildPlacementPayload({
                planId: incomingPlan.id,
                transform: preview.transform,
                sortOrder: placements.length,
                pairing: isBasePlacement
                    ? null
                    : {
                          placedPlanId: placements.find((p) => p.id === anchor.placementId)?.plan_id,
                          placedSurfaceUuid: anchorId,
                          surfaceUuid: incomingId,
                          thicknessM: Number.isFinite(thicknessM) ? thicknessM : DEFAULT_WALL_THICKNESS_M,
                          slideM: Number.isFinite(slideM) ? slideM : 0,
                          flipped,
                      },
            });
            const refreshed = await HouseFloorPlanService.addPlacement(floorPlan.id, payload);
            setFloorPlan(refreshed);
            toast.success('Room placed');
            resetAddFlow();
        } catch (err) {
            toast.error(err.message || 'Failed to place room');
        } finally {
            setSaving(false);
        }
    };

    // Re-align: re-solve the baked transform from the stored Pairing
    // against the rooms' CURRENT geometry.
    const handleRealign = async (placement) => {
        const pairing = placement.pairing;
        if (!pairing || placement.is_unpinned) return;
        setRealigningId(placement.id);
        try {
            const anchorPlacement = placements.find((p) => p.plan_id === pairing.placed_plan_id);
            const anchorSurface = anchorCandidates.find(
                (c) => c.identifier === pairing.placed_surface_uuid && c.placementId === anchorPlacement?.id,
            );
            const plan = await UserFloorPlanService.getPlan(placement.plan_id);
            const incomingSurface = pairableSurfaces(plan.data).find(
                (c) => c.identifier === pairing.surface_uuid,
            );
            if (!anchorSurface || !incomingSurface) {
                toast.error('The paired wall no longer exists — re-pair this room');
                return;
            }
            const transform = solvePairingTransform({
                placedSurface: anchorSurface.surface,
                incomingSurface: incomingSurface.surface,
                thicknessM: pairing.thickness_m ?? DEFAULT_WALL_THICKNESS_M,
                slideM: pairing.slide_m ?? 0,
                flipped: Boolean(pairing.flipped),
                placedInteriorPoint: placementInteriorPoint(floorPlan?.data, anchorPlacement?.id),
            });
            const refreshed = await HouseFloorPlanService.updatePlacement(floorPlan.id, placement.id, {
                transform,
            });
            setFloorPlan(refreshed);
            toast.success(`${placement.room_name} re-aligned`);
        } catch (err) {
            toast.error(err.message || 'Failed to re-align room');
        } finally {
            setRealigningId(null);
        }
    };

    const handleRemovePlacement = async () => {
        if (!placementToRemove) return;
        removingRef.current = true; // synchronous: blocks the same-tick onClose
        setRemoving(true);
        try {
            await HouseFloorPlanService.removePlacement(floorPlan.id, placementToRemove.id);
            await fetchFloorPlan();
            toast.success(`${placementToRemove.room_name} removed from floor`);
            setPlacementToRemove(null);
        } catch (err) {
            toast.error(err.message || 'Failed to remove room');
        } finally {
            removingRef.current = false;
            setRemoving(false);
        }
    };

    const handleDeletePlan = async () => {
        deletingPlanRef.current = true; // synchronous: blocks the same-tick onClose
        setDeletingPlan(true);
        try {
            await HouseFloorPlanService.deleteFloorPlan(floorPlan.id);
            toast.success('Floor plan deleted');
            navigate(`/houses/${floorPlan.house_id}?tab=rooms`);
        } catch (err) {
            toast.error(err.message || 'Failed to delete floor plan');
            deletingPlanRef.current = false;
            setDeletingPlan(false);
        }
    };

    const handleRenameLabel = async () => {
        const next = labelDraft.trim();
        setEditingLabel(false);
        if (!next || next === floorPlan.floor_label) return;
        try {
            await HouseFloorPlanService.updateFloorPlan(floorPlan.id, { floor_label: next });
            setFloorPlan((fp) => ({ ...fp, floor_label: next }));
        } catch (err) {
            toast.error(err.message || 'Failed to rename floor');
        }
    };

    const staleCount = placements.filter((p) => p.is_stale).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 text-[var(--text-muted)]">
                <Loader2 size={28} className="animate-spin" />
            </div>
        );
    }
    if (error || !floorPlan) {
        return (
            <div className="max-w-xl mx-auto mt-12 text-center text-[var(--text-secondary)]">
                <AlertCircle size={40} className="mx-auto mb-3 text-red-500" />
                <p>{error || 'Floor plan not found'}</p>
            </div>
        );
    }

    return (
        <div className="px-4 md:px-8 pb-8">
            {/* Header */}
            <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3 min-w-0">
                    <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={ArrowLeft}
                        onClick={() => navigate(`/houses/${floorPlan.house_id}?tab=rooms`)}
                    >
                        {house?.name || 'House'}
                    </Button>
                    {editingLabel ? (
                        <Input
                            autoFocus
                            className="max-w-xs"
                            maxLength={64}
                            value={labelDraft}
                            onChange={(e) => setLabelDraft(e.target.value)}
                            onBlur={handleRenameLabel}
                            onKeyDown={(e) => e.key === 'Enter' && handleRenameLabel()}
                        />
                    ) : (
                        <h1 className="text-2xl font-bold text-[var(--text-main)] truncate flex items-center gap-2">
                            {floorPlan.floor_label}
                            <button
                                onClick={() => {
                                    setLabelDraft(floorPlan.floor_label);
                                    setEditingLabel(true);
                                }}
                                className="text-[var(--text-muted)] hover:text-[var(--primary)]"
                                aria-label="Rename floor"
                            >
                                <Pencil size={16} />
                            </button>
                        </h1>
                    )}
                    {floorPlan.floor_area != null && (
                        <span className="text-sm text-[var(--text-secondary)] hidden md:inline">
                            {formatArea(floorPlan.floor_area, unitSystem)}
                        </span>
                    )}
                </div>
                <Button variant="destructive" size="sm" leadingIcon={Trash2} onClick={() => setShowDeletePlan(true)}>
                    Delete
                </Button>
            </div>

            {staleCount > 0 && !adding && (
                <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-sm flex items-center gap-2">
                    <AlertTriangle size={16} />
                    {staleCount === 1 ? 'A room plan has' : `${staleCount} room plans have`} changed since this floor
                    was assembled — use Re-align to refresh positions.
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Viewer */}
                <div className="lg:col-span-2 h-[60vh] lg:h-[75vh] rounded-2xl overflow-hidden bg-[var(--bg-card)]">
                    <FloorPlanErrorBoundary>
                        <FloorPlanViewer
                            roomId={null}
                            previewData={viewerData}
                            className="h-full"
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            onSceneDataLoad={setSceneData}
                            hiddenIds={hiddenIds}
                            unitSystem={unitSystem}
                            onUnitSystemChange={setUnitSystem}
                        />
                    </FloorPlanErrorBoundary>
                </div>

                {/* Panel */}
                <div className="space-y-4 lg:h-[75vh] lg:overflow-y-auto">
                    {!adding ? (
                        <>
                            <Card className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="font-bold text-[var(--text-main)]">Rooms on this floor</h3>
                                    <Button size="sm" leadingIcon={Plus} onClick={() => setAdding(true)}>
                                        Add Room
                                    </Button>
                                </div>
                                {placements.length === 0 && (
                                    <p className="text-sm text-[var(--text-muted)]">
                                        Place a first room to start assembling this floor.
                                    </p>
                                )}
                                <div className="space-y-2">
                                    {placements.map((p) => (
                                        <div
                                            key={p.id}
                                            className="px-3 py-2 rounded-lg bg-[var(--bg-hover)] flex items-center gap-2"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-[var(--text-main)] truncate">
                                                    {p.room_name}
                                                </p>
                                                <p className="text-xs text-[var(--text-muted)] truncate">
                                                    {p.plan_name}
                                                    {p.is_unpinned && ' • needs re-pairing'}
                                                </p>
                                            </div>
                                            {p.is_stale && (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    leadingIcon={realigningId === p.id ? Loader2 : RefreshCw}
                                                    disabled={p.is_unpinned || realigningId !== null || !p.pairing}
                                                    onClick={() => handleRealign(p)}
                                                    title={
                                                        p.is_unpinned
                                                            ? 'The paired wall no longer exists'
                                                            : 'Re-solve this room’s position from its pairing'
                                                    }
                                                >
                                                    Re-align
                                                </Button>
                                            )}
                                            <button
                                                onClick={() => setPlacementToRemove(p)}
                                                className="text-[var(--text-muted)] hover:text-red-500 p-1"
                                                aria-label={`Remove ${p.room_name}`}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </Card>

                            {sceneData && (
                                <SceneObjectList
                                    objects={sceneData.objects}
                                    walls={sceneData.walls}
                                    doors={sceneData.doors}
                                    windows={sceneData.windows}
                                    floors={sceneData.floors}
                                    openings={sceneData.openings}
                                    unknown={sceneData.unknown}
                                    floorArea={sceneData.floorArea}
                                    floorAreaSource={sceneData.floorAreaSource}
                                    floorFootprintM={sceneData.floorFootprintM}
                                    floorSections={sceneData.floorSections}
                                    wallMeasurements={sceneData.wallMeasurements}
                                    selectedId={selectedId}
                                    onSelect={setSelectedId}
                                    hiddenIds={hiddenIds}
                                    onToggleItem={(itemId) =>
                                        setHiddenIds((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(itemId)) next.delete(itemId);
                                            else next.add(itemId);
                                            return next;
                                        })
                                    }
                                    onToggleSection={(ids, makeVisible) =>
                                        setHiddenIds((prev) => {
                                            const next = new Set(prev);
                                            ids.forEach((itemId) => {
                                                if (makeVisible) next.delete(itemId);
                                                else next.add(itemId);
                                            });
                                            return next;
                                        })
                                    }
                                    unitSystem={unitSystem}
                                />
                            )}
                        </>
                    ) : (
                        <Card className="p-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-bold text-[var(--text-main)]">Add Room</h3>
                                <Button variant="ghost" size="sm" onClick={resetAddFlow}>
                                    Cancel
                                </Button>
                            </div>

                            {/* Step 1: pick the room */}
                            {!incomingPlan && (
                                <>
                                    <StepHeading>1. Which room joins this floor?</StepHeading>
                                    {loadingPlan ? (
                                        <div className="py-6 text-center text-[var(--text-muted)]">
                                            <Loader2 size={20} className="mx-auto animate-spin" />
                                            <p className="text-xs mt-2">Preparing room plan…</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-1 max-h-72 overflow-y-auto">
                                            {candidateRooms.map((room) => (
                                                <button
                                                    key={room.id}
                                                    onClick={() => handlePickRoom(room)}
                                                    className="w-full px-3 py-2 rounded-lg text-left text-sm bg-[var(--bg-hover)] text-[var(--text-main)] hover:text-[var(--primary)] transition-colors"
                                                >
                                                    {room.name}
                                                    {room.floor && (
                                                        <span className="text-xs text-[var(--text-muted)] ml-2">
                                                            {room.floor}
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                            {candidateRooms.length === 0 && (
                                                <p className="text-sm text-[var(--text-muted)]">
                                                    Every room of this house is already placed.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Base room: no pairing needed */}
                            {incomingPlan && isBasePlacement && (
                                <>
                                    <StepHeading>2. Place the first room</StepHeading>
                                    <p className="text-sm text-[var(--text-secondary)] mb-4">
                                        The first room anchors the floor — every other room is paired
                                        against one already placed.
                                    </p>
                                    <Button fullWidth leadingIcon={Check} loading={saving} onClick={handleConfirmPlacement}>
                                        Place {incomingPlan.name}
                                    </Button>
                                </>
                            )}

                            {/* Steps 2-4: pairing */}
                            {incomingPlan && !isBasePlacement && (
                                <div className="space-y-4">
                                    <div>
                                        <StepHeading>2. Shared wall or door on the floor</StepHeading>
                                        <SurfacePickList
                                            candidates={anchorCandidates}
                                            selectedId={anchorId}
                                            onPick={(pick) => {
                                                setAnchorId(pick);
                                                setSelectedId(pick);
                                            }}
                                            unitSystem={unitSystem}
                                        />
                                    </div>
                                    {anchor && (
                                        <div>
                                            <StepHeading>3. The same feature in {incomingPlan.name}</StepHeading>
                                            <SurfacePickList
                                                candidates={incomingCandidates}
                                                selectedId={incomingId}
                                                onPick={(pick) => {
                                                    setIncomingId(pick);
                                                    setSelectedId(pick);
                                                }}
                                                unitSystem={unitSystem}
                                            />
                                        </div>
                                    )}
                                    {preview && (
                                        <div>
                                            <StepHeading>4. Adjust &amp; confirm</StepHeading>
                                            {preview.overlaps && (
                                                <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs flex items-center gap-2">
                                                    <AlertTriangle size={14} />
                                                    Rooms overlap noticeably — check the pairing or flip the room.
                                                </div>
                                            )}
                                            <div className="grid grid-cols-2 gap-3 mb-3">
                                                <label className="text-xs text-[var(--text-secondary)]">
                                                    Wall thickness (m)
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="1"
                                                        value={thicknessIn}
                                                        onChange={(e) => setThicknessIn(e.target.value)}
                                                    />
                                                </label>
                                                <label className="text-xs text-[var(--text-secondary)]">
                                                    Slide along wall (m)
                                                    <Input
                                                        type="number"
                                                        step="0.1"
                                                        value={slideIn}
                                                        onChange={(e) => setSlideIn(e.target.value)}
                                                    />
                                                </label>
                                            </div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm text-[var(--text-main)]">Flip room</span>
                                                <Toggle enabled={flipped} onChange={setFlipped} ariaLabel="Flip room" />
                                            </div>
                                            <div className="flex items-center justify-between mb-4">
                                                <span className="text-sm text-[var(--text-main)]">Other side of wall</span>
                                                <Toggle enabled={otherSide} onChange={setOtherSide} ariaLabel="Other side of wall" />
                                            </div>
                                            <Button fullWidth leadingIcon={Check} loading={saving} onClick={handleConfirmPlacement}>
                                                Place room
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isOpen={placementToRemove !== null}
                // Guard against the modal's same-tick onClose with a REF (not
                // the `removing` state, which is still false this tick). Stays
                // open showing "Removing…" until the handler clears state.
                onClose={() => !removingRef.current && setPlacementToRemove(null)}
                onConfirm={handleRemovePlacement}
                isLoading={removing}
                title={`Remove ${placementToRemove?.room_name}?`}
                message={
                    <>
                        Remove <strong>{placementToRemove?.room_name}</strong> from this floor plan? The room and
                        its plan are not deleted — only its position on this floor.
                    </>
                }
                confirmText={removing ? 'Removing…' : 'Remove'}
                isDanger
                icon={Trash2}
            />
            <ConfirmationModal
                isOpen={showDeletePlan}
                // Ref guard (see remove modal): stays open during the async
                // delete; navigates away on success.
                onClose={() => !deletingPlanRef.current && setShowDeletePlan(false)}
                onConfirm={handleDeletePlan}
                isLoading={deletingPlan}
                title={`Delete the ${floorPlan.floor_label} floor plan?`}
                message="The assembled floor layout will be deleted. Your rooms, scans and room plans are untouched."
                confirmText={deletingPlan ? 'Deleting…' : 'Delete'}
                isDanger
                icon={Trash2}
            />
        </div>
    );
};

export default HouseFloorPlanEditor;
